export interface SEOData {
  title?: string;
  description?: string;
  keywords?: string[];
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
}

export interface FamiliesOverviewResponse {
  totalFamilies: number;
  families: Array<{
    familySlug: string;
    family: string;
    totalContentItems: number;
    description?: string;
  }>;
}

export interface FamilyLandingPagesResponse {
  family: string;
  totalContentItems: number;
  generatedLandingPages: number;
  landingPages: JobLandingPage[];
}

export interface CVUploadResponse {
  success: boolean;
  message: string;
  uploadId?: string;
  analysisResults?: {
    extractedText: string;
    matchingScore?: number;
    recommendations?: string[];
  };
}

export interface JobLandingPage {
  contentId: string;
  slug: string;
  title: string;
  url: string;
  metaDescription: string;
  keywords: string[];
  domain?: string;
  family?: string;
  domainSlug?: string;
  familySlug?: string;
  familySlugs?: string[];
  jobSlugs?: string[];
  experienceLevel?: string;
  skills?: string[];
  industries?: string[];
  generatedAt: string;
  status: string;
  html?: string;
  seoData?: SEOData;
}

export interface JobLandingPagesResponse {
  totalJobContentItems: number;
  generatedLandingPages: number;
  pages: JobLandingPage[];
}

export interface JobLandingPageResponse {
  contentId: string;
  slug: string;
  title: string;
  url: string;
  metaDescription: string;
  keywords: string[];
  domainSlug?: string;
  familySlug?: string;
  familySlugs?: string[];
  jobSlugs?: string[];
  html: string;
  seoData: SEOData;
  generatedAt: string;
  status: string;
}

export interface DomainLandingPageResponse {
  domain: string;
  title: string;
  metaDescription: string;
  keywords: string[];
  domainSlug?: string;
  familySlug?: string;
  familySlugs?: string[];
  jobSlugs?: string[];
  html: string;
  seoData: SEOData;
  generatedAt: string;
  status: string;
}

export interface FamilyLandingPageResponse {
  contentId: string;
  slug: string;
  title: string;
  url: string;
  metaDescription: string;
  keywords: string[];
  domainSlug?: string;
  familySlug?: string;
  familySlugs?: string[];
  jobSlugs?: string[];
  html: string;
  seoData: SEOData;
  generatedAt: string;
  status: string;
}

export interface FamilyLandingPagesResponse {
  totalFamilyContentItems: number;
  generatedLandingPages: number;
  pages: JobLandingPage[];
}

export interface DomainLandingPagesResponse {
  domain: string;
  totalContentItems: number;
  generatedLandingPages: number;
  landingPages: JobLandingPage[];
}

export interface DomainContentItem {
  id: string;
  slug: string;
  title: string;
  type: string;
  status: string;
  domain?: string;
  family?: string;
  domainSlug?: string;
  familySlug?: string;
  domainSlugs?: string[];
  familySlugs?: string[];
  jobSlugs?: string[];
}

export interface DomainInfo {
  domain: string;
  totalContentItems: number;
  contentTypes: {
    type: string;
    count: number;
  }[];
  slugs: {
    slug: string;
    title: string;
    type: string;
    family?: string;
    domainSlug?: string;
    familySlug?: string;
    familySlugs?: string[];
    jobSlugs?: string[];
    experienceLevel?: string;
    status: string;
  }[];
}

export interface DomainsOverviewResponse {
  totalDomains: number;
  totalContentItems: number;
  domains: DomainInfo[];
}

class TalentyApiService {
  private baseUrl: string;

  constructor() {
    // Configure API base URL via env with safe fallback to production backend
    this.baseUrl = import.meta.env.VITE_API_BASE_URL ||
      `https://ts.talenty.ai`;
  }

  /**
   * Get all available job landing pages
   */
  async getAllJobLandingPages(): Promise<JobLandingPagesResponse> {
    const response = await fetch(`${this.baseUrl}/api/public/landing-pages/jobs`);
    
    if (!response.ok) {
      throw new Error(`Die Stellenseiten konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get a specific job landing page by slug
   */
  async getJobLandingPageBySlug(slug: string): Promise<JobLandingPageResponse> {
    const encodedSlug = encodeURIComponent(slug);
    const requestUrl = `${this.baseUrl}/api/public/landing-pages/job/${encodedSlug}`;
    
    const response = await fetch(requestUrl);
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Die Stellenseite „${slug}“ wurde nicht gefunden.`);
      }
      throw new Error(`Die Stellenseite konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get all available domains with overview information
   */
  async getDomainsOverview(): Promise<DomainsOverviewResponse> {
    const response = await fetch(`${this.baseUrl}/api/public/landing-pages/domains-overview`);
    
    if (!response.ok) {
      throw new Error(`Die Bereichsübersicht konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get all domain content items overview (ID, slug, type)
   */
  async getDomainContentItemsOverview(): Promise<DomainContentItem[]> {
    const response = await fetch(`${this.baseUrl}/api/public/landing-pages/domain-content-items-overview`);
    
    if (!response.ok) {
      throw new Error(`Die Bereichsinhalte konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get landing pages for a specific domain
   */
  async getLandingPagesByDomain(domain: string): Promise<DomainLandingPagesResponse> {
    const response = await fetch(`${this.baseUrl}/api/public/landing-pages/domains/${encodeURIComponent(domain)}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Der Bereich „${domain}“ wurde nicht gefunden.`);
      }
      throw new Error(`Die Seiten des Bereichs konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get a specific domain landing page with HTML content by slug
   */
  async getDomainLandingPageBySlug(slug: string): Promise<DomainLandingPageResponse> {
    const response = await fetch(`${this.baseUrl}/api/public/landing-pages/domain-slug/${encodeURIComponent(slug)}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Die Bereichsseite „${slug}“ wurde nicht gefunden.`);
      }
      throw new Error(`Die Bereichsseite konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get a specific domain landing page with HTML content by domain name
   */
  async getDomainLandingPage(domain: string): Promise<DomainLandingPageResponse> {
    const response = await fetch(`${this.baseUrl}/api/public/landing-pages/domain/${encodeURIComponent(domain)}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Die Bereichsseite „${domain}“ wurde nicht gefunden.`);
      }
      throw new Error(`Die Bereichsseite konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get all available family landing pages
   */
  async getAllFamilyLandingPages(): Promise<FamilyLandingPagesResponse> {
    const response = await fetch(`${this.baseUrl}/api/public/landing-pages/families`);
    
    if (!response.ok) {
      throw new Error(`Die Jobfamilienseiten konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get a specific family landing page by slug
   */
  async getFamilyLandingPageBySlug(slug: string): Promise<FamilyLandingPageResponse> {
    const response = await fetch(`${this.baseUrl}/api/public/landing-pages/family/${encodeURIComponent(slug)}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Die Jobfamilienseite „${slug}“ wurde nicht gefunden.`);
      }
      throw new Error(`Die Jobfamilienseite konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get families overview
   */
  async getFamiliesOverview(): Promise<FamiliesOverviewResponse> {
    const response = await fetch(`${this.baseUrl}/api/public/landing-pages/families-overview`);
    
    if (!response.ok) {
      throw new Error(`Die Jobfamilienübersicht konnte nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get family content items overview
   */
  async getFamilyContentItemsOverview(): Promise<DomainContentItem[]> {
    const response = await fetch(`${this.baseUrl}/api/public/landing-pages/family-content-items-overview`);
    
    if (!response.ok) {
      throw new Error(`Die Jobfamilieninhalte konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Get landing pages for a specific family
   */
  async getLandingPagesByFamily(family: string): Promise<FamilyLandingPagesResponse> {
    const response = await fetch(`${this.baseUrl}/api/public/landing-pages/families/${encodeURIComponent(family)}`);
    
    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(`Die Jobfamilie „${family}“ wurde nicht gefunden.`);
      }
      throw new Error(`Die Seiten der Jobfamilie konnten nicht geladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }

  /**
   * Upload CV/Resume PDF to backend webhook
   */
  async uploadCV(file: File): Promise<CVUploadResponse> {
    const formData = new FormData();
    formData.append('cv', file);
    
    const response = await fetch(`${this.baseUrl}/api/cv/upload`, {
      method: 'POST',
      body: formData,
    });
    
    if (!response.ok) {
      throw new Error(`Der Lebenslauf konnte nicht hochgeladen werden (Status ${response.status}).`);
    }
    
    return response.json();
  }
}

export const talentyApi = new TalentyApiService();
